import { Doughnut } from "react-chartjs-2";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { ArcElement, Chart as ChartJS, Tooltip } from "chart.js";
import { Button, Col, Flex, Row, theme } from "antd";
import CardSkeleton from "../../../../../../../../atom/CardSkeleton";

import StylesProvider from "./style";

import {
  getCurrencyValueWithRoundSign,
  percentIncrease,
} from "../../../../../../../../utils/helper/general";
import {
  getLatestSavedProposalProps,
  getManagedUsersTotalProposedAmount,
} from "../../../../../../../../utils/helper/specialized";
import { accumulateProposalByProductUsage } from "../../../../../admin/helper";

import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { ReactComponent as Edit } from "./image/edit.svg";

ChartJS.register(ArcElement, Tooltip);

const SectionAnalytics = ({
  quarterly,
  quarterAnalytics,
  listAllProposals,
  loading,
  managedUsers,
  onClickEditGoals,
  permissions,
  radialBarSeries,
}) => {
  const { token } = theme.useToken();

  const proposedAumPercentIncrease = percentIncrease({
    compareValue:
      getManagedUsersTotalProposedAmount(
        quarterAnalytics.currentQuarterProposals
      ) ?? 0,
    compareWithValue:
      getManagedUsersTotalProposedAmount(
        quarterAnalytics.lastQuarterProposals
      ) ?? 0,

    maxPercent: 100,
    minPercent: -100,
  });

  const lastQuarterPercentIncrease = percentIncrease({
    compareValue: quarterAnalytics.currentQuarterProposals?.length ?? 0,
    compareWithValue: quarterAnalytics.lastQuarterProposals?.length ?? 0,
    maxPercent: 100,
    minPercent: -100,
  });

  const getTopProductsSeries = () => {
    if (!listAllProposals?.length) return [];

    const top3SelectedProducts = accumulateProposalByProductUsage({
      listAllProposals,
    }).slice(0, 3);

    return [
      ...top3SelectedProducts,
      {
        name: "Rest",
        y:
          listAllProposals?.length -
          top3SelectedProducts.reduce((acc, it) => acc + it.y, 0),
      },
    ];
  };

  const options = {
    chart: {
      type: "pie",
      height: 134,
      width: 150,
      backgroundColor: "transparent",
    },
    xAxis: {
      visible: false,
    },
    tooltip: {
      outside: true,
      formatter: function () {
        return `${this.key}: <b>${this.y}</b> time(s)`;
      },
    },
    title: {
      text: null,
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        size: 112,
        innerSize: "60%",
        dataLabels: {
          enabled: false,
        },
        center: ["50%", "50%"],
        borderColor: "#ffffff",
        borderWidth: 2,
      },
    },
    exporting: {
      buttons: {
        contextButton: {
          enabled: false,
        },
      },
    },
    colors: token.top_products_pie_chart_pallet,
    series: {
      data: getTopProductsSeries(),
    },
  };

  return (
    <StylesProvider token={token}>
      <Flex gap={24} style={{ width: "100%" }}>
        <div className="statistic-card">
          <Flex align="center" justify="space-between">
            <b className="value">
              {getCurrencyValueWithRoundSign(
                getManagedUsersTotalProposedAmount(
                  quarterAnalytics.currentQuarterProposals
                )
              )}
            </b>
            <b
              style={{
                fontSize: 20,
                color:
                  proposedAumPercentIncrease < 0
                    ? token.color_error
                    : token.color_green,
              }}
            >
              {proposedAumPercentIncrease}%{" "}
              {proposedAumPercentIncrease < 0 ? (
                <ArrowDownOutlined />
              ) : (
                <ArrowUpOutlined />
              )}
            </b>
          </Flex>
          <b>Proposed this quarter</b>
          <p className="description">
            {getCurrencyValueWithRoundSign(
              getManagedUsersTotalProposedAmount(
                quarterAnalytics.lastQuarterProposals
              )
            )}{" "}
            this time last quarter
          </p>
        </div>
        <div className="statistic-card">
          <Flex align="center" justify="space-between">
            <b className="value">
              {quarterAnalytics.currentQuarterProposals?.length ?? 0}
            </b>
            <b
              style={{
                fontSize: 20,
                color:
                  lastQuarterPercentIncrease < 0
                    ? token.color_error
                    : token.color_green,
              }}
            >
              {lastQuarterPercentIncrease}%{" "}
              {lastQuarterPercentIncrease < 0 ? (
                <ArrowDownOutlined />
              ) : (
                <ArrowUpOutlined />
              )}
            </b>
          </Flex>
          <b>Proposals this quarter</b>
          <p className="description">
            {quarterAnalytics.lastQuarterProposals?.length ?? 0} this time last
            quarter
          </p>
        </div>
        <div className="statistic-card">
          <Flex gap={16} style={{ fontSize: 23, textAlign: "center" }} vertical>
            <div>
              <b>
                {
                  managedUsers?.filter(
                    it =>
                      it?.valueMap?.leadInitial?.isConverted ||
                      it.userType === "Registered"
                  )?.length
                }
              </b>{" "}
              <span style={{ fontWeight: 300 }}>Clients</span>
            </div>

            <div>
              <b>
                {
                  managedUsers?.filter(
                    it =>
                      !it?.valueMap?.leadInitial?.isConverted &&
                      it.userType !== "Registered"
                  )?.length
                }
              </b>{" "}
              <span style={{ fontWeight: 300 }}>Leads</span>
            </div>
          </Flex>
        </div>
      </Flex>

      {/*<Flex gap={23} style={{ width: "100%" }}>*/}
      {/*  <CardSkeleton loading={loading} width={470} height={196}>*/}
      {/*    <Row*/}
      {/*      style={{*/}
      {/*        width: "66%",*/}
      {/*        background: "#ffffff",*/}
      {/*        padding: "10px 12px",*/}
      {/*        borderRadius: 8,*/}
      {/*        boxShadow: "0px 0px 16px #0000000D",*/}
      {/*      }}*/}
      {/*    >*/}
      {/*      <Col span={12} style={{ padding: 12 }}>*/}
      {/*        {radialBarSeries?.length && (*/}
      {/*          <Doughnut*/}
      {/*            data={{*/}
      {/*              labels: [],*/}
      {/*              datasets: radialBarSeries,*/}
      {/*            }}*/}
      {/*            options={{*/}
      {/*              circumference: 360,*/}
      {/*              borderColor: "white",*/}
      {/*              borderRadius: 10,*/}
      {/*              borderWidth: 3,*/}
      {/*              cutout: "50%",*/}
      {/*              hoverBorderColor: "transparent",*/}
      {/*              plugins: {*/}
      {/*                legend: {*/}
      {/*                  display: false,*/}
      {/*                },*/}
      {/*                tooltip: {*/}
      {/*                  callbacks: {*/}
      {/*                    label: it =>*/}
      {/*                      `${it.dataset.label}: ${it.formattedValue}%`,*/}
      {/*                    footer: tooltipItems => {*/}
      {/*                      if (tooltipItems[0].dataIndex === 1) {*/}
      {/*                        return "* left to reach Goal";*/}
      {/*                      }*/}
      {/*                    },*/}
      {/*                  },*/}
      {/*                  displayColors: false,*/}
      {/*                },*/}
      {/*              },*/}
      {/*            }}*/}
      {/*          />*/}
      {/*        )}*/}
      {/*      </Col>*/}
      {/*      <Col span={12}>*/}
      {/*        <Flex vertical gap={2}>*/}
      {/*          <Flex justify="end">*/}
      {/*            <Button*/}
      {/*              disabled={!permissions?.allowSetGoals}*/}
      {/*              icon={<Edit />}*/}
      {/*              onClick={onClickEditGoals}*/}
      {/*              shape="round"*/}
      {/*              style={{ background: "#E7EBEE", padding: "0 4px" }}*/}
      {/*            />*/}
      {/*          </Flex>*/}

      {/*          <h3*/}
      {/*            style={{*/}
      {/*              fontSize: 24,*/}
      {/*              color: "#1E1919",*/}
      {/*              margin: "0 0 9px",*/}
      {/*            }}*/}
      {/*          >*/}
      {/*            Quarterly Goals*/}
      {/*          </h3>*/}
      {/*          <Flex align="center" gap={4}>*/}
      {/*            <div*/}
      {/*              className="marker"*/}
      {/*              style={{*/}
      {/*                background: token.quarterly_goal_proposal_color,*/}
      {/*              }}*/}
      {/*            />*/}
      {/*            <span style={{ color: token.quarterly_goal_proposal_color }}>*/}
      {/*              {quarterAnalytics.currentQuarterProposals?.length}/*/}
      {/*              {quarterly?.proposals ?? 0} Proposals Generated*/}
      {/*            </span>*/}
      {/*          </Flex>*/}
      {/*          <Flex align="center" gap={4}>*/}
      {/*            <div*/}
      {/*              className="marker"*/}
      {/*              style={{ background: token.quarterly_goal_client_color }}*/}
      {/*            />*/}
      {/*            <span style={{ color: token.quarterly_goal_client_color }}>*/}
      {/*              {quarterAnalytics.currentQuarterConversions?.length}/*/}
      {/*              {quarterly?.clientConversion ?? 0} Client Conversions*/}
      {/*            </span>*/}
      {/*          </Flex>*/}
      {/*          <Flex align="center" gap={4}>*/}
      {/*            <div*/}
      {/*              className="marker"*/}
      {/*              style={{ background: token.quarterly_goal_aum_color }}*/}
      {/*            />*/}
      {/*            <span style={{ color: token.quarterly_goal_aum_color }}>*/}
      {/*              {getCurrencyValueWithRoundSign(*/}
      {/*                quarterAnalytics.currentQuarterProposals?.reduce(*/}
      {/*                  (acc, elem) =>*/}
      {/*                    acc +*/}
      {/*                    (getLatestSavedProposalProps(*/}
      {/*                      elem.valueMap?.productMap*/}
      {/*                    )?.investmentAmount ?? +elem.proposedAmount),*/}
      {/*                  0*/}
      {/*                )*/}
      {/*              )}*/}
      {/*              /{getCurrencyValueWithRoundSign(quarterly?.newAUM)} New AUM*/}
      {/*            </span>*/}
      {/*          </Flex>*/}
      {/*        </Flex>*/}
      {/*      </Col>*/}
      {/*    </Row>*/}
      {/*  </CardSkeleton>*/}
      {/*  <Flex*/}
      {/*    align="center"*/}
      {/*    style={{*/}
      {/*      width: "33%",*/}
      {/*      background: "#ffffff",*/}
      {/*      paddingTop: 18,*/}
      {/*      borderRadius: 8,*/}
      {/*      boxShadow: "0px 0px 16px #0000000D",*/}
      {/*    }}*/}
      {/*    vertical*/}
      {/*  >*/}
      {/*    <b>Top 3 Products</b>*/}
      {/*    <span>vs. the rest</span>*/}

      {/*    {getTopProductsSeries().length && (*/}
      {/*      <HighchartsReact highcharts={Highcharts} options={options} />*/}
      {/*    )}*/}
      {/*  </Flex>*/}
      {/*</Flex>*/}
    </StylesProvider>
  );
};

export default SectionAnalytics;
